import { isEqual } from 'lodash';
import { StoredTabManagerState, TabManagerStorateAdapter } from './storage';
import { ParamsProvider, TabManagerState } from './tab-manager';
import { RenderedTab, TabConfig, TabMatch } from './types';
import { findMatchingTab } from './utils';

export type TabManagerAction =
  | { type: 'SET_ACTIVE_TAB'; payload: string | null }
  | {
      type: 'REORDER_TABS';
      payload: { newOrder: string[]; tabs: Record<string, RenderedTab> };
    }
  | {
      type: 'PUSH_TAB';
      payload: {
        route: string;
        title?: string;
        params?: Record<string, string | undefined>;
      };
    }
  | { type: 'CLOSE_TAB'; payload: { tabId: string } }
  | {
      type: 'PUSH_AND_REPLACE_TAB';
      payload: {
        route: string;
        title?: string;
        params?: Record<string, string | undefined>;
      };
    }
  | {
      type: 'UPDATE_TAB';
      payload: { tabId: string; params: Record<string, string | number> };
    }
  | {
      type: 'SYNC';
      payload: {
        activeTab: string | null;
        tabs: string[];
      } | null;
    }
  | {
      type: 'CLOSE_ALL_TABS';
    };

// Helper function to create a new rendered tab
const createRenderedTab = (
  route: string,
  title: string | undefined,
  displayTitle: string | undefined,
  icon: string | undefined,
  tabParams: Record<string, string | undefined>,
  renderedTab: React.ReactNode,
  Wrapper: React.ComponentType<{ children: React.ReactNode }>,
  Fallback: React.ComponentType<{ children?: React.ReactNode }>,
  order: number
): RenderedTab => ({
  route,
  id: route,
  tabContentView: (
    <ParamsProvider
      tabId={route}
      params={{
        ...tabParams,
        order,
        route,
        tabId: route,
      }}
    >
      <Wrapper>{renderedTab ?? <Fallback />}</Wrapper>
    </ParamsProvider>
  ),
  title: title ?? route,
  displayTitle,
  icon,
  params: {
    ...tabParams,
    tabId: route,
    order,
    route,
  },
  order,
});

// Helper function to add history entry
const addHistoryEntry = (
  state: TabManagerState,
  activeTab: string | null
): TabManagerState['history'] => [
  ...state.history,
  {
    activeTab,
    renderedTabs: Object.keys(state.renderedTabs),
  },
];

// Helper function to find next active tab when closing a tab
const findNextActiveTab = (
  renderedTabs: Record<string, RenderedTab>,
  history: { activeTab: string | null; renderedTabs: string[] }[]
): string | null => {
  if (history.length === 0) {
    return null;
  }
  const nextActiveTab = history.at(-1)?.activeTab;
  if (nextActiveTab && renderedTabs[nextActiveTab] !== undefined) {
    return nextActiveTab;
  }
  return findNextActiveTab(renderedTabs, history.slice(0, -1));
};

// Helper function to check if tab params are subset
const isTabParamsSubset = (
  prevParams: Record<string, any>,
  newParams: Record<string, any>
): boolean => {
  return Object.keys(newParams).every(
    (key) => prevParams[key] === newParams[key]
  );
};

// Helper function to create tab params
const createTabParams = (
  match: TabMatch | null,
  params: Record<string, string | undefined> | undefined,
  id: string
): Record<string, string | undefined> => ({
  ...(match?.params ?? {}),
  ...(params ?? {}),
  key: id,
});

// Action handlers
const handleSetActiveTab = (
  state: TabManagerState,
  payload: string | null,
  allTabConfigs: TabConfig[],
  wrapper: React.ComponentType<{ children: React.ReactNode }>,
  fallback: React.ComponentType<{ children?: React.ReactNode }>
): TabManagerState => {
  const isTabRendered = (payload: string) =>
    state.renderedTabs[payload] !== undefined;
  if (payload !== null && !isTabRendered(payload)) {
    const newState = handlePushTab(
      state,
      {
        route: payload,
      },
      allTabConfigs,
      wrapper,
      fallback
    );

    return {
      ...newState,
      activeTab: payload,
      history: addHistoryEntry(state, payload),
    };
  }

  return {
    ...state,
    activeTab: payload,
    history: addHistoryEntry(state, payload),
  };
};

const handleCloseTab = (
  state: TabManagerState,
  payload: { tabId: string }
): TabManagerState => {
  const renderedTabs = Object.fromEntries(
    Object.entries(state.renderedTabs).filter(([id]) => id !== payload.tabId)
  );

  const nextActiveTab = findNextActiveTab(renderedTabs, state.history);

  return {
    ...state,
    activeTab: nextActiveTab,
    renderedTabs,
    history: addHistoryEntry(state, nextActiveTab),
  };
};

const handleReorderTabs = (
  state: TabManagerState,
  payload: { newOrder: string[]; tabs: Record<string, RenderedTab> }
): TabManagerState => ({
  ...state,
  renderedTabs: payload.tabs,
});

const handlePushTab = (
  state: TabManagerState,
  payload: {
    route: string;
    title?: string;
    displayTitle?: string;
    icon?: string;
    params?: Record<string, string | undefined>;
  },
  allTabConfigs: TabConfig[],
  wrapper: React.ComponentType<{ children: React.ReactNode }>,
  fallback: React.ComponentType<{ children?: React.ReactNode }>
): TabManagerState => {
  const { route, title, displayTitle, icon, params } = payload;
  const match = findMatchingTab(allTabConfigs, route);

  if (!match) {
    // eslint-disable-next-line no-console
    console.warn(`No matching tab configuration found for route: ${route}`);
  }

  const id = route;
  const tabParams = createTabParams(match, params, id);
  const numTabs = Object.keys(state.renderedTabs).length;

  // Check if tab already exists with same params
  if (state.renderedTabs[id] !== undefined) {
    const { tabId: _, ...prevParams } = state.renderedTabs[id]!.params;
    if (isTabParamsSubset(prevParams, tabParams)) {
      return {
        ...state,
        activeTab: id,
        renderedTabs: state.renderedTabs,
        history: addHistoryEntry(state, id),
      };
    }
  }

  const renderedTab = match?.element;
  const newRenderedTab = createRenderedTab(
    route,
    title,
    displayTitle,
    icon,
    tabParams,
    renderedTab,
    wrapper,
    fallback,
    numTabs
  );

  const newRenderedTabs = {
    ...state.renderedTabs,
    [id]: newRenderedTab,
  };

  return {
    ...state,
    activeTab: id,
    renderedTabs: newRenderedTabs,
    history: addHistoryEntry(state, id),
  };
};

const handlePushAndReplaceTab = (
  state: TabManagerState,
  payload: {
    route: string;
    title?: string;
    displayTitle?: string;
    icon?: string;
    params?: Record<string, string | undefined>;
  },
  allTabConfigs: TabConfig[],
  wrapper: React.ComponentType<{ children: React.ReactNode }>,
  fallback: React.ComponentType<{ children?: React.ReactNode }>
): TabManagerState => {
  const { route, title, displayTitle, icon, params } = payload;

  // Find any existing tab with the same route
  const existingTabId = Object.entries(state.renderedTabs).find(
    ([_, t]) => t.route === route
  )?.[0];

  // If there's an active tab and it's different from the existing tab with same route
  if (
    state.activeTab &&
    (!existingTabId || existingTabId !== state.activeTab)
  ) {
    // Close the current active tab first
    const filteredTabs = Object.fromEntries(
      Object.entries(state.renderedTabs).filter(
        ([id]) => id !== state.activeTab
      )
    );

    // Now push the new tab
    const match = findMatchingTab(allTabConfigs, route);

    if (!match) {
      // eslint-disable-next-line no-console
      console.warn(`No matching tab configuration found for route: ${route}`);
    }

    const id = route;
    const tabParams = createTabParams(match, params, id);
    const numTabs = Object.keys(filteredTabs).length;

    const renderedTab = match?.element;

    const newRenderedTab = createRenderedTab(
      route,
      title,
      displayTitle,
      icon,
      tabParams,
      renderedTab,
      wrapper,
      fallback,
      numTabs
    );

    const newRenderedTabs = {
      ...filteredTabs,
      [id]: newRenderedTab,
    };

    return {
      ...state,
      activeTab: id,
      renderedTabs: newRenderedTabs,
      history: addHistoryEntry(state, id),
    };
  } else {
    return handlePushTab(state, payload, allTabConfigs, wrapper, fallback);
  }
};

const handleUpdateTab = (
  state: TabManagerState,
  payload: { tabId: string; params: Record<string, string | number> }
): TabManagerState => {
  const areTabParamsEqual = (
    tabId: string,
    params: Record<string, string | number>
  ) => {
    const tab = state.renderedTabs[tabId]!;
    return isEqual(tab.params, params);
  };

  if (areTabParamsEqual(payload.tabId, payload.params)) {
    return state;
  }

  return {
    ...state,
    renderedTabs: {
      ...state.renderedTabs,
      [payload.tabId]: {
        ...state.renderedTabs[payload.tabId]!,
        params: {
          ...state.renderedTabs[payload.tabId]!.params,
          ...payload.params,
        },
      },
    },
  };
};

const handleSync = (
  state: TabManagerState,
  payload: {
    activeTab: string | null;
    tabs: string[];
  } | null,
  allTabConfigs: TabConfig[],
  wrapper: React.ComponentType<{ children: React.ReactNode }>,
  fallback: React.ComponentType<{ children?: React.ReactNode }>
): TabManagerState => {
  if (payload === null) {
    return {
      activeTab: null,
      renderedTabs: {},
      history: [],
    };
  }

  const getOrMakeTab = (route: string): RenderedTab => {
    const renderedTab = state.renderedTabs[route];

    if (renderedTab) {
      return renderedTab;
    }

    const { renderedTabs: nextRenderedTabs } = handlePushTab(
      state,
      {
        route,
      },
      allTabConfigs,
      wrapper,
      fallback
    );

    return nextRenderedTabs[route]!;
  };

  return {
    activeTab: payload.activeTab,
    renderedTabs: Object.fromEntries(
      payload.tabs.map((route) => {
        return [route, getOrMakeTab(route)];
      })
    ),
    history: state.history,
  };
};

const handleCloseAllTabs = (
  state: TabManagerState,
  storageAdapter: TabManagerStorateAdapter
): TabManagerState => {
  storageAdapter.setState({
    activeTab: null,
    tabs: [],
  });
  return { ...state, renderedTabs: {}, history: [] };
};

// Main reducer function
export const tabManagerReducer =
  (
    allTabConfigs: TabConfig[],
    wrapper: React.ComponentType<{ children: React.ReactNode }>,
    fallback: React.ComponentType<{ children?: React.ReactNode }>,
    storageAdapter: TabManagerStorateAdapter
  ) =>
  (state: TabManagerState, action: TabManagerAction): TabManagerState => {
    const makeNextState = () => {
      switch (action.type) {
        case 'SET_ACTIVE_TAB':
          return handleSetActiveTab(
            state,
            action.payload,
            allTabConfigs,
            wrapper,
            fallback
          );
        case 'CLOSE_TAB':
          return handleCloseTab(state, action.payload);
        case 'REORDER_TABS':
          return handleReorderTabs(state, action.payload);
        case 'PUSH_TAB':
          return handlePushTab(
            state,
            action.payload,
            allTabConfigs,
            wrapper,
            fallback
          );
        case 'PUSH_AND_REPLACE_TAB':
          return handlePushAndReplaceTab(
            state,
            action.payload,
            allTabConfigs,
            wrapper,
            fallback
          );
        case 'UPDATE_TAB':
          return handleUpdateTab(state, action.payload);
        case 'SYNC':
          return handleSync(
            state,
            action.payload,
            allTabConfigs,
            wrapper,
            fallback
          );
        case 'CLOSE_ALL_TABS':
          return handleCloseAllTabs(state, storageAdapter);
        default:
          return state;
      }
    };

    const nextState = makeNextState();

    if (action.type !== 'SYNC') {
      //if its a SYNC, then the state is driven by the storageAdapter so no need to sync back
      storageAdapter.setState(convertTabManagerStateToStoredState(nextState));
    }

    return nextState;
  };

const convertTabManagerStateToStoredState = (
  state: TabManagerState
): StoredTabManagerState => {
  return {
    activeTab: state.activeTab,
    tabs: Object.values(state.renderedTabs).map((tab) => tab.route),
  };
};
